/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Help } from '@carbon/icons-react';
import { Button } from '@carbon/react';

import { PropertiesPanelPopup } from 'features/properties-panel-popup';
import { currentDiagramStore, organizationStore } from 'stores';
import { FeelCopilotPanel, useIsFeelCopilotEnabled } from 'experiments/ai-features/feel-copilot/';
import { trackingService } from 'services';
import { ValidationStatus, isNonEmpty } from 'features/properties-panel-feel-playground/utils/helpers';
import { useAreAiFeaturesEnabled } from 'experiments/hooks';
import { evaluateFeelExpression } from 'features/properties-panel-feel-playground/utils/evaluateFeelExpression';

import { JsonEditorPanel } from './json-editor/';
import { ResultViewerPanel } from './result-viewer/';
import { Wrapper, LeftPane, RightPane, Splitter } from './PropertiesPanelFeelPlayground.styled';
import { FeelEditorPanel } from './feel-editor';
import { trackFeelExpressionEval } from './utils/tracking';
import { useHorizontalSplitter } from './hooks/use-horizontal-splitter';
import { useCollapsibleGroup } from './hooks/use-collapsible-group';

const SPLITTER_CONSTRAINTS = {
  leftPaneMinWidth: 300,
  rightPaneMinWidth: 200
};

/**
 * @typedef {Object} Position
 * @property {number} x - X coordinate
 * @property {number} y - Y coordinate
 */

/** @typedef {import('./result-viewer/ResultViewerPanel').ValidationState} ValidationState */

/**
 * @typedef {Object} PropertiesPanelFeelPlaygroundProps
 * @property {() => void} onClose - Callback when popup is closed
 * @property {string} location - a location used for Mixpanel tracking (e.g., "bpmnCanvas")
 * @property {Object} element - The BPMN element being interacted
 */
const PropertiesPanelFeelPlayground = (props) => {
  const { value: initialExpression, onClose, onInput, title = 'FEEL Playground', location, element } = props;

  const wrapperRef = useRef(null);
  const { width: rightPaneWidth, handleMouseDown: handleSplitterMouseDown } = useHorizontalSplitter(
    wrapperRef,
    300,
    SPLITTER_CONSTRAINTS
  );

  const feelEditorRef = useRef(null);
  const abortControllerRef = useRef(null);

  const isFeelCopilotEnabled = useIsFeelCopilotEnabled();
  const areAiFeaturesEnabled = useAreAiFeaturesEnabled();

  const [expression, setExpression] = useState(initialExpression);
  const [expressionFromFeelCopilot, setExpressionFromFeelCopilot] = useState('');
  const [expressionContext, setExpressionContext] = useState(null);

  const leftPaneConfigs = useMemo(() => {
    const configs = [{ initialCollapsed: false, expandedHeight: '50¯%' }];
    if (isFeelCopilotEnabled) {
      configs.push({ initialCollapsed: true, expandedHeight: '50%' });
    }
    return configs;
  }, [isFeelCopilotEnabled]);

  const rightPaneConfigs = useMemo(
    () => [
      { initialCollapsed: false, expandedHeight: '50%' },
      { initialCollapsed: false, expandedHeight: '50%' }
    ],
    []
  );

  const [feelEditorProps, feelCopilotProps] = useCollapsibleGroup(leftPaneConfigs);
  const [jsonEditorProps, resultViewerProps] = useCollapsibleGroup(rightPaneConfigs);

  const [expressionValidation, setExpressionValidation] = useState(
    /** @type {ValidationState} */ ({
      status: ValidationStatus.IDLE
    })
  );
  const [expressionContextValidation, setExpressionContextValidation] = useState(
    /** @type {ValidationState} */ ({
      status: ValidationStatus.IDLE
    })
  );

  const [evaluationState, setEvaluationState] = useState(
    /** @type {ValidationState} */ ({
      status: ValidationStatus.IDLE
    })
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      feelEditorRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setExpression(initialExpression);
    feelEditorRef.current?.setValue(initialExpression);
  }, [initialExpression]);

  const onFeelEditorInput = useCallback(
    (value) => {
      setExpression(value);
      setExpressionFromFeelCopilot('');
      onInput(value);
    },
    [onInput]
  );

  const onUseExpressionClick = useCallback(
    (newExpression) => {
      if (feelEditorProps.isCollapsed) {
        feelEditorProps.onToggleCollapsed();
      }
      setExpression(newExpression);
      setExpressionFromFeelCopilot(newExpression);
      trackingService.trackFeelCopilotUseExpression({ from: location });
    },
    [location, feelEditorProps]
  );

  const onUseContextClick = useCallback(
    (newContext) => {
      if (jsonEditorProps.isCollapsed) {
        jsonEditorProps.onToggleCollapsed();
      }

      setExpressionContext(JSON.stringify(newContext, null, 2));
    },
    [jsonEditorProps]
  );

  const evaluateExpression = useCallback(
    async ({ expression, contextJson }) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      await evaluateFeelExpression({
        expression,
        contextJson,
        setResult: (state) => {
          setEvaluationState(state);
          trackFeelExpressionEval(state, element);
        },
        signal: controller.signal
      });
    },
    [element]
  );

  const pendingRef = useRef({ expression: '', contextJson: '' });

  useEffect(() => {
    pendingRef.current = { expression, contextJson: expressionContext };
  }, [expression, expressionContext]);

  const shouldEvaluate = useMemo(() => {
    return (
      expressionValidation.status !== ValidationStatus.ERROR &&
      expressionContextValidation.status !== ValidationStatus.ERROR &&
      expressionValidation.status !== ValidationStatus.LOADING &&
      expressionContextValidation.status !== ValidationStatus.LOADING &&
      isNonEmpty(expression)
    );
  }, [expressionValidation, expressionContextValidation, expression]);

  useEffect(() => {
    if (shouldEvaluate) {
      evaluateExpression({ expression, contextJson: expressionContext });
    } else {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setEvaluationState({ status: ValidationStatus.IDLE, result: null, warnings: [], error: null });
    }
  }, [shouldEvaluate, expressionContext, evaluateExpression, expression]);

  return (
    <PropertiesPanelPopup
      title={title}
      onClose={onClose}
      initialSize={{ width: 800, height: 600 }}
      sizeConstraints={{ minWidth: 700, minHeight: 500, maxWidth: 100000, maxHeight: 100000 }}
      headerItemsRight={
        <Button
          hasIconOnly
          iconDescription="Learn more about the FEEL playground."
          renderIcon={Help}
          size="md"
          kind="ghost"
          href="https://docs.camunda.io/docs/next/components/modeler/feel/feel-playground/"
          target="_blank"
          tooltipPosition="bottom"
          aria-label="Learn more about the FEEL playground."
        />
      }
    >
      <Wrapper ref={wrapperRef}>
        <LeftPane style={{ minWidth: SPLITTER_CONSTRAINTS.leftPaneMinWidth }}>
          <FeelEditorPanel
            initialValue={expression}
            placeholder=""
            onInput={onFeelEditorInput}
            ref={feelEditorRef}
            validation={expressionValidation}
            onValidationChange={setExpressionValidation}
            expressionFromFeelCopilot={expressionFromFeelCopilot}
            {...feelEditorProps}
          />
          {isFeelCopilotEnabled ? (
            <FeelCopilotPanel
              expression={expression}
              context={expressionContext}
              modelerInjector={currentDiagramStore.modeler?.get?.('injector')}
              hasElevatedOrganizationPermissions={organizationStore.hasElevatedOrganizationPermissions}
              areAiFeaturesEnabled={areAiFeaturesEnabled}
              organizationManagementPageUrl={organizationStore.organizationManagementPageUrl}
              onUseExpressionClick={onUseExpressionClick}
              onUseContextClick={onUseContextClick}
              location={location}
              {...feelCopilotProps}
            />
          ) : null}
        </LeftPane>
        <Splitter data-test="splitter" onMouseDown={handleSplitterMouseDown} />
        <RightPane data-test="right-pane" style={{ width: `${rightPaneWidth}px` }}>
          <JsonEditorPanel
            value={expressionContext}
            onChange={setExpressionContext}
            validation={expressionContextValidation}
            onValidationChange={setExpressionContextValidation}
            placeholder="Add variables in JSON format"
            {...jsonEditorProps}
          />
          <ResultViewerPanel state={evaluationState} {...resultViewerProps} />
        </RightPane>
      </Wrapper>
    </PropertiesPanelPopup>
  );
};

export { PropertiesPanelFeelPlayground };
